import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { defaultColors, styled } from "../../stitches.config";
import { ReactComponent as SearchIcon } from "../assets/icons/search.svg?react";
import { body, bodyLarge, h4, h3, h4Strong } from "../commonStyles/Fonts";
import axios from "axios";
import debounce from "lodash.debounce";
import { IArticle } from "../types/Article";
import ArticleListItem from "../components/ArticleListItem";
import { useNavigate, useLocation } from "react-router-dom";
import Media from "../../components/common/Media";
import Link from "../../components/Link";
import Footer from "../components/Footer"
import useMediaQuery from "../../helpers/useMediaQuery";


export const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const SearchPage = () => {

  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [results, setResults] = useState<IArticle[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [nextPage, setNextPage] = useState<string>('');
  const [isInView, setIsInView] = useState<boolean>(false);
  const query = useQuery();
  const navigate = useNavigate();
  const querySearch = query.get("q");
  const inputRef = useRef<any>();

  const isDesktop = useMediaQuery('bp4');

  const [isSortingActive, setSortingActive] = useState(false);
  const toggleSortingActive = () => {
    setSortingActive(!isSortingActive)
  }

  const [sortingValue, setSortingValue] = useState("Najnowsze");
  const [orderValue, setOrderValue] = useState("desc");

  const lastElement = useRef<any>(null);

  const handleChange = (e: any) => {
    setSearchValue(e.target.value);
  };

  useEffect(() => {
    if (inputRef?.current) {
      // @ts-ignore
      inputRef.current.value = querySearch;
    }
    setSearchValue(querySearch);
  }, [querySearch]);

  // pobieranie pierwszych wyników
  const getResults = useCallback(debounce(value => {
    if (value || searchValue) {
      axios
        .get(
          `${window.API_HOST}/pwa-search-cse/?q=${encodeURIComponent(
            value
          )}&count=10&startIndex=1&order=${orderValue}`
        )
        .then(({ data }) => {
          navigate({
            pathname: "/search",
            search: `?q=${value}`,
          });
          setResults(data.results);
          setNextPage(data.next_page);
        })
        .then(() => {
          setIsLoading(false);
        })
        .catch((err) => console.log(err))
    } else {
      navigate({
        pathname: "/search",
        search: `?q=`,
      });
      setResults([]);
    }
  }, 500), [sortingValue])

  useEffect(() => {
    setIsLoading(true);
    getResults(searchValue);
  }, [searchValue, orderValue]);

  // observer ostatniego elementu w viewporcie
  const callback = (entries: any) => {
    const [entry] = entries;
    setIsInView(entry.isIntersecting);
  }

  useEffect(() => {
    let observer = new IntersectionObserver(callback);
    if (lastElement.current) {
      observer.observe(lastElement.current)
    }
    return () => {
      if (lastElement.current) {
        observer.unobserve(lastElement.current);
      }
    }
  }, [callback])

  //dołaodwywanie wyników
  const infiniteScroll = () => {
    if (nextPage && !isLoading) {
      setIsLoading(true);
      axios.get(nextPage)
        .then(({ data }) => {
          setResults([...results, ...data.results]);
          setNextPage(`${data.next_page}&order=${orderValue}`);
        })
        .then(() => {
          setIsLoading(false);
        })
        .catch((err) => console.log(err))
    }
  }

  useEffect(() => {
    infiniteScroll();
  }, [isInView])

  const clearResults = () => {
    setResults([]);
    setSearchValue('');
    setNextPage('');
    setIsLoading(false);
    setIsInView(false);
    navigate({
      pathname: "/search",
      search: `?q=`,
    });
  }

  const handleSortChange = (e: any) => {
    const value = e.target.value
    switch (value) {
      case 'oldest':
        setSortingValue("Najstarsze");
        setOrderValue('asc');
        setResults([]);
        break;
      case 'newest':
        setSortingValue("Najnowsze");
        setOrderValue('desc')
        setResults([]);
        break;
      default:
        setSortingValue("Trafność");
        setOrderValue('')
        setResults([]);
        break;
    }
    toggleSortingActive()
  }

  return (
    <>
      <Container>
        <SearchField>
          <SearchIcon />
          <SearchInput
            ref={inputRef}
            className={h4()}
            placeholder="Szukaj"
            // value={searchValue || ""}
            onChange={handleChange}
          />
          <ClearButton className={body()} onClick={() => clearResults()}>
            Wyczyść
          </ClearButton>
        </SearchField>
        <DetailsWrapper className={bodyLarge()}>
          <p>Wynik wyszukiwania: {results && results.length > 0 ? results.length : 0}</p>
          <SortingWrapper>
            {isDesktop && <p>Sortuj:</p>}
            <SortingButton onClick={toggleSortingActive} value={sortingValue}>
              <span>
                {sortingValue + " "}
                <svg width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.68 5.52253C3.71888 5.5676 3.76773 5.6039 3.82306 5.62882C3.87839 5.65374 3.93881 5.66666 4 5.66666C4.06119 5.66666 4.12161 5.65374 4.17694 5.62882C4.23227 5.6039 4.28112 5.5676 4.32 5.52253L7.92 0.943822C7.96457 0.887134 7.99171 0.819727 7.99838 0.749153C8.00505 0.67858 7.99099 0.607628 7.95777 0.544249C7.92455 0.48087 7.87348 0.427567 7.81029 0.390314C7.7471 0.35306 7.67428 0.333328 7.6 0.333328L0.4 0.333328C0.325715 0.333328 0.252898 0.35306 0.189708 0.390314C0.126517 0.427567 0.0754504 0.48087 0.0422293 0.544249C0.0090081 0.607628 -0.00505472 0.67858 0.00161647 0.749153C0.00828766 0.819727 0.0354293 0.887134 0.0800002 0.943822L3.68 5.52253Z" />
                </svg>
              </span>
            </SortingButton>
          </SortingWrapper>
          {isSortingActive &&
            <>
              <SortingNavBackground />
              <SortingNav className={h4()}>
                <div className={h3()}>
                  Sortuj
                  <button onClick={toggleSortingActive}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M11.4605 10.3451L17.9546 3.851L16.4226 2.31893L9.92842 8.81307L3.43441 2.31906L1.90234 3.85112L8.39636 10.3451L1.90234 16.8392L3.43441 18.3712L9.92842 11.8772L16.4226 18.3713L17.9546 16.8393L11.4605 10.3451ZM9.92842 9.16663L16.4226 2.67248L16.4226 2.67253L9.92847 9.16667L9.92842 9.16663ZM3.43441 2.67261L3.43445 2.67266L2.25594 3.85117L2.2559 3.85112L3.43441 2.67261ZM8.74991 10.3451L2.2559 16.8392L2.25594 16.8392L8.74996 10.3452L8.74991 10.3451Z" />
                    </svg>
                  </button>
                </div>
                <SortingNavButton onClick={handleSortChange} value='accuracy' className={sortingValue === "Trafność" ? "active" : ""}>
                  Trafność
                  <span>
                    <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M19.4766 2.34394L7.16669 13.8612L0.533203 7.32465L1.99668 5.84192L7.2053 10.9721L18.0533 0.822632L19.4766 2.34394Z" />
                    </svg>
                  </span>
                </SortingNavButton>
                <SortingNavButton onClick={handleSortChange} value='newest' className={sortingValue === "Najnowsze" ? "active" : ""}>
                  Najnowsze
                  <span>
                    <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M19.4766 2.34394L7.16669 13.8612L0.533203 7.32465L1.99668 5.84192L7.2053 10.9721L18.0533 0.822632L19.4766 2.34394Z" />
                    </svg>
                  </span>
                </SortingNavButton>
                <SortingNavButton onClick={handleSortChange} value='oldest' className={sortingValue === "Najstarsze" ? "active" : ""}>
                  Najstarsze
                  <span>
                    <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M19.4766 2.34394L7.16669 13.8612L0.533203 7.32465L1.99668 5.84192L7.2053 10.9721L18.0533 0.822632L19.4766 2.34394Z" />
                    </svg>
                  </span>
                </SortingNavButton>
              </SortingNav>
            </>
          }
        </DetailsWrapper>
        <Results>
          {
            results.length > 0 && results.map(({ title, url, picture, date, category, iso_date, author }, idx) => {
              const isLastElement = results.length - 1 == idx;
              return isLastElement ? (
                <div ref={lastElement} className="last" key={idx}>
                  <Link to={url} key={idx}>
                    <ArticleWrapper>
                      <ArticleImage>
                        {isDesktop ?
                          <Media
                            src={picture.img}
                            alt={picture.alt}
                            height={200}
                            width={300}
                            media={picture.media}
                            preserveRatio={false}
                          /> :
                          <Media
                            src={picture.img}
                            alt={picture.alt}
                            height={72}
                            width={108}
                            media={picture.media}
                          />
                        }
                      </ArticleImage>
                      <h4 className={isDesktop ? h4Strong() : bodyLarge()}>{title}</h4>
                      <ArticleDetails className={body()}>
                        {" "}
                        {date && (
                          <>
                            <time dateTime={iso_date}>
                              {date.split(',')[0]}
                            </time>
                            {" | "}
                          </>
                        )}
                        {author.name}
                      </ArticleDetails>
                    </ArticleWrapper>
                  </Link>
                </div>
              ) : (
                <div>
                  <Link to={url} key={idx}>
                    <ArticleWrapper>
                      <ArticleImage>
                        {isDesktop ?
                          <Media
                            src={picture.img}
                            alt={picture.alt}
                            height={200}
                            width={300}
                            media={picture.media}
                            preserveRatio={false}
                          /> :
                          <Media
                            src={picture.img}
                            alt={picture.alt}
                            height={72}
                            width={108}
                            media={picture.media}
                          />
                        }
                      </ArticleImage>
                      <h4 className={isDesktop ? h4Strong() : bodyLarge()}>{title}</h4>
                      <ArticleDetails className={body()}>
                        {" "}
                        {date && (
                          <>
                            <time dateTime={iso_date}>
                              {date.split(',')[0]}
                            </time>
                            {" | "}
                          </>
                        )}
                        {author.name}
                      </ArticleDetails>
                    </ArticleWrapper>
                  </Link>
                </div>
              )
            }
            )
          }
        </Results>
      </Container>
      <Footer />
    </>
  );
};

export default SearchPage;

const Container = styled("div", {
  backgroundColor: "$grayscale0",
  padding: "34px 16px 36px 16px",
  "@bp4": {
    padding: "71px 84px 78px 84px",
  },
  a: {
    color: "$grayscale75",
  },
});

const SearchField = styled("div", {
  padding: "20px",
  border: "1px solid $grayscale50",
  display: "grid",
  gridTemplateColumns: "30px auto 60px",
  svg: {
    path: {
      fill: '$grayscale100',
    }
  },
  "@bp4": {
    width: "624px",
  },
});

const SearchInput = styled("input", {
  color: "$grayscale100",
  background: "none",
  border: "none",
  "&:placeholder": {
    color: defaultColors.grayscale75,
  },
});

const ClearButton = styled("button", {
  background: "none",
  border: "none",
  color: defaultColors.grayscale75,
  "&:hover": {
    color: defaultColors.grayscale100,
  },
});

const DetailsWrapper = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: "8px",
  marginTop: "32px",
  borderBottom: "1px solid $grayscale25",
  "@bp4": {
    paddingBottom: "0px",
    marginTop: "48px",
    borderWidth: 0,
  },
  p: {
    color: "$grayscale75",
  },
});

const Results = styled("div", {
  "@bp4": {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
  },
  "@bp5": {
    gridTemplateColumns: "1fr 1fr 1fr",
  },
  "@media(min-width: 1675px)": {
    gridTemplateColumns: "1fr 1fr 1fr 1fr"
  },
  "a": {
    marginTop: "24px",
    display: "block",
    "@bp4": {
      marginTop: "0px",
    }
  },
  "h4": {
    color: "$grayscale100",
    margin: "0 0 0 16px",
    height: "72px",
    overflow: "hidden",
    "@bp4": {
      margin: "16px 0 4px 0",
    }
  }
});

const ArticleImage = styled("div", {
  width: '108px',
  height: '72px',
  "@bp4": {
    width: '100%',
    height: 'auto',
  },
  "& img": {
    width: "100%",
    height: "100%",
    "@bp4": {
      objectFit: "cover",
    },
  },
});

const ArticleWrapper = styled("div", {
  width: "100%",
  display: "flex",
  alignItems: "center",

  "@bp4": {
    width: "auto",
    display: "block",
    h4: {
      marginBottom: "4px",
    },
  },
});

const ArticleDetails = styled("span", {
  display: "none",
  color: "$grayscale75",
  "@bp4": {
    display: "block",
  },
});

const SortingWrapper = styled("div", {
  display: "flex",
  "& p": {
    marginRight: "6px",
    color: "$grayscale75",
  }
});

const SortingButton = styled("button", {
  borderWidth: "0px",
  background: "none",
  color: "$grayscale100",
  "& span": {
    marginLeft: "4px",
    "& svg": {
      marginBottom: "2px",
      fill: "$grayscale100",
    }
  }
});

const SortingNav = styled("div", {
  position: "fixed",
  bottom: 0,
  left: 0,
  zIndex: 1000000,
  width: "100%",
  background: "$grayscale0",
  "& .active": {
    color: "$primary",
    "& span": {
      display: "block"
    }
  },
  "& div": {
    padding: "24px 16px",
    display: "flex",
    justifyContent: "space-between",
    color: "$grayscale100",
    "& button": {
      background: "none",
      borderWidth: "0",
    },
    "& svg": {
      fill: "$grayscale100"
    }
  }
});

const SortingNavButton = styled("button", {
  width: "100%",
  padding: "16px",
  border: "0px",
  borderTop: "1px solid $grayscale25",
  background: "$grayscale0",
  textAlign: "left",
  color: "$grayscale100",
  display: "flex",
  justifyContent: "space-between",
  "&:hover": {
    color: "$primary",
  },
  "& span": {
    display: "none",
    "& svg": {
      fill: "$primary",
    }
  }
});

const SortingNavBackground = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "$grayscale100Fixed",
  opacity: "0.75",
  zIndex: 99,
});